"use client";

import { ProFeatureLockOverlay } from "@/components/ProFeatureLockOverlay";
import {
  TestForm,
  type TestCreationStep,
  type TestGenerationContext,
  type TestSource,
} from "@/components/TestForm";
import { TestResult } from "@/components/TestResult";
import {
  ANALYTICS_EVENTS,
  analyticsErrorType,
  track,
} from "@/lib/analyticsEvents";
import type {
  LessonAnalysis,
  TestConfig,
  TestOutput,
  TestType,
} from "@/lib/testTypes";
import { cn } from "@/lib/utils";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { useAction, useMutation, useQuery } from "convex/react";
import {
  AlertCircle,
  CheckCircle2,
  ClipboardCheck,
  Download,
  Library,
  KeyRound,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Trash2,
} from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Suspense,
} from "react";

const LOADING_MESSAGES_ANALYSIS = [
  "Reading your lesson content",
  "Identifying the skills you taught",
  "Preparing questions from your lesson",
];

const LOADING_MESSAGES_GENERATION = [
  "Selecting appropriate skills",
  "Building the assessment sections",
  "Balancing marks and difficulty",
  "Preparing the answer key",
  "Formatting the printable test",
];

type MeaningfulProgress = {
  source: TestSource | null;
  step: TestCreationStep;
};

function durationMinutes(testType: TestType): number {
  return testType === "quiz" ? 30 : 60;
}

function sectionCount(config: TestConfig): number {
  return [
    config.comprehension.included,
    config.language.included,
    config.writing.included,
  ].filter(Boolean).length;
}

function NewTestContent() {
  const searchParams = useSearchParams();
  const resumeId = searchParams.get("resume") as Id<"testGenerations"> | null;
  const me = useQuery(api.users.getMe);
  const creditInfo = useQuery(api.creditRequests.getMine);
  const priorTests = useQuery(api.testGenerations.getUserTestGenerations);
  const resumedTest = useQuery(api.testGenerations.getTestGeneration, resumeId ? { id: resumeId } : "skip");
  const analyseAction = useAction(api.actions.analyseLesson.analyseLesson);
  const generateAction = useAction(api.actions.generateTest.generateTest);
  const acceptTest = useMutation(api.testGenerations.acceptTest);
  const updateTeachingProfile = useMutation(api.users.updateTeachingProfile);
  const deleteUploadedFiles = useMutation(api.files.deleteUploadedFiles);

  const [analysis, setAnalysis] = useState<LessonAnalysis | null>(null);
  const [testGenerationId, setTestGenerationId] = useState<Id<"testGenerations"> | null>(null);
  const [pendingOutput, setPendingOutput] = useState<TestOutput | null>(null);
  const [acceptedOutput, setAcceptedOutput] = useState<TestOutput | null>(null);
  const [lessonImageIds, setLessonImageIds] = useState<Id<"_storage">[]>([]);
  const [lastConfig, setLastConfig] = useState<TestConfig | null>(null);
  const [lastContext, setLastContext] = useState<TestGenerationContext | null>(null);
  const [analysing, setAnalysing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [acceptBusy, setAcceptBusy] = useState(false);
  const [regenerateCount, setRegenerateCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [messageIndex, setMessageIndex] = useState(0);
  const [formVersion, setFormVersion] = useState(0);
  const [activeStep, setActiveStep] = useState<TestCreationStep>("lesson_content");
  const [activeSource, setActiveSource] = useState<TestSource | null>(null);
  const generationInFlight = useRef(false);
  const progressRef = useRef<MeaningfulProgress>({ source: null, step: "lesson_content" });
  const generatedRef = useRef(false);
  const abandonmentTracked = useRef(false);
  const creationTracked = useRef(false);
  const resumeHydrated = useRef(false);

  const billing = creditInfo?.billing;
  const canUseTests = billing?.hasProGatedFeatureAccess ?? false;
  const planTier = billing?.hasActivePro
    ? "pro"
    : billing?.trialEligible
      ? "trial"
      : billing?.subscriptionPlan ?? "none";

  const isFirstTest = priorTests === undefined
    ? null
    : !priorTests.some((test) => test.status === "accepted");

  /* eslint-disable react-hooks/set-state-in-effect -- hydrate the explicitly requested server-owned draft exactly once */
  useEffect(() => {
    if (!resumeId || !resumedTest || resumeHydrated.current) return;
    resumeHydrated.current = true;
    setTestGenerationId(resumedTest._id);
    setLessonImageIds(resumedTest.lessonImageIds);
    try {
      if (resumedTest.detectedComponents) setAnalysis(JSON.parse(resumedTest.detectedComponents) as LessonAnalysis);
      if (resumedTest.output) {
        const output = JSON.parse(resumedTest.output) as TestOutput;
        if (resumedTest.status === "accepted") setAcceptedOutput(output);
        else setPendingOutput(output);
      }
      if (resumedTest.selectedConfig) {
        const config = JSON.parse(resumedTest.selectedConfig) as TestConfig;
        setLastConfig(config);
        setLastContext({ source: "existing_lesson", fileCount: resumedTest.lessonImageIds.length, grade: resumedTest.grade });
      }
    } catch {
      setError("This saved test draft could not be read. Its stored record has not been changed.");
    }
    setActiveSource("existing_lesson");
    setActiveStep(resumedTest.output ? "generate" : "test_setup");
    setFormVersion((version) => version + 1);
  }, [resumeId, resumedTest]);
  /* eslint-enable react-hooks/set-state-in-effect */

  useEffect(() => {
    if (creationTracked.current || isFirstTest === null || creditInfo === undefined) return;
    creationTracked.current = true;
    track(ANALYTICS_EVENTS.testsPageViewed);
    track(ANALYTICS_EVENTS.testCreationStarted, {
      is_first_test: isFirstTest,
      plan_tier: planTier,
    });
  }, [creditInfo, isFirstTest, planTier]);

  useEffect(() => {
    const shouldWarn = () =>
      Boolean(progressRef.current.source && !generatedRef.current);
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!shouldWarn()) return;
      event.preventDefault();
      event.returnValue = "";
    };
    const captureAbandonment = () => {
      const current = progressRef.current;
      if (
        abandonmentTracked.current ||
        generatedRef.current ||
        !current.source ||
        isFirstTest === null
      ) return;
      abandonmentTracked.current = true;
      track(ANALYTICS_EVENTS.testCreationAbandoned, {
        step: current.step,
        source: current.source,
        is_first_test: isFirstTest,
      });
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", captureAbandonment);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", captureAbandonment);
      captureAbandonment();
    };
  }, [isFirstTest]);

  const loadingMessages = analysing
    ? LOADING_MESSAGES_ANALYSIS
    : LOADING_MESSAGES_GENERATION;

  useEffect(() => {
    if (!analysing && !generating) return;
    const timer = window.setInterval(() => {
      setMessageIndex((index) => (index + 1) % loadingMessages.length);
    }, 1800);
    return () => window.clearInterval(timer);
  }, [analysing, generating, loadingMessages.length]);

  const gate = useMemo(() => {
    if (me === undefined || creditInfo === undefined || isFirstTest === null) return "loading" as const;
    if (me === null) return "sync" as const;
    return "ready" as const;
  }, [creditInfo, isFirstTest, me]);

  const onAnalyse = useCallback(async (payload: {
    lessonImageIds: Id<"_storage">[];
    grade: string;
    testType: TestType;
  }): Promise<boolean> => {
    setError(null);
    setAnalysing(true);
    setMessageIndex(0);
    try {
      track(ANALYTICS_EVENTS.testAnalyseClicked, {
        testType: payload.testType,
        grade: payload.grade,
        imageCount: payload.lessonImageIds.length,
      });
      const result = await analyseAction(payload);
      const parsed = JSON.parse(result.analysis) as LessonAnalysis;
      track(ANALYTICS_EVENTS.testAnalyseSucceeded, {
        testType: payload.testType,
        grade: payload.grade,
      });
      setAnalysis(parsed);
      setTestGenerationId(result.testGenerationId);
      setLessonImageIds(payload.lessonImageIds);
      return true;
    } catch (caught) {
      track(ANALYTICS_EVENTS.testAnalyseFailed, {
        errorType: analyticsErrorType(caught),
        testType: payload.testType,
      });
      setError(caught instanceof Error ? caught.message : "We could not prepare this lesson. Try again.");
      return false;
    } finally {
      setAnalysing(false);
    }
  }, [analyseAction]);

  const runGeneration = useCallback(async (
    config: TestConfig,
    context: TestGenerationContext,
    isRetry: boolean,
  ): Promise<boolean> => {
    if (!testGenerationId || generationInFlight.current || isFirstTest === null) return false;
    generationInFlight.current = true;
    setError(null);
    setGenerating(true);
    setMessageIndex(0);
    if (!isRetry) setPendingOutput(null);
    setLastConfig(config);
    setLastContext(context);
    const startedAt = Date.now();
    const safeProperties = {
      source: context.source,
      test_type: config.testType,
      grade_level: context.grade,
      difficulty: config.complexity,
      duration_minutes: durationMinutes(config.testType),
      section_count: sectionCount(config),
      file_count: context.fileCount,
      is_first_test: isFirstTest,
      plan_tier: planTier,
    } as const;
    track(ANALYTICS_EVENTS.testGenerationStarted, safeProperties);
    track(ANALYTICS_EVENTS.testGenerateClicked, {
      testType: config.testType,
      grade: context.grade,
    });
    try {
      const result = await generateAction({
        testGenerationId,
        selectedConfig: JSON.stringify(config),
      });
      const parsed = JSON.parse(result.output) as TestOutput;
      track(ANALYTICS_EVENTS.testGenerated, {
        source: context.source,
        test_type: config.testType,
        grade_level: context.grade,
        generation_duration_ms: Date.now() - startedAt,
        is_first_test: isFirstTest,
      });
      track(ANALYTICS_EVENTS.testGenerateSucceeded, {
        testType: config.testType,
        grade: parsed.grade,
      });
      setPendingOutput(parsed);
      generatedRef.current = true;
      if (!isRetry) setRegenerateCount(0);
      return true;
    } catch (caught) {
      const errorCategory = analyticsErrorType(caught);
      track(ANALYTICS_EVENTS.testGenerationFailed, {
        source: context.source,
        test_type: config.testType,
        error_category: errorCategory,
        is_first_test: isFirstTest,
      });
      track(ANALYTICS_EVENTS.testGenerateFailed, {
        errorType: errorCategory,
        testType: config.testType,
      });
      setError(caught instanceof Error ? caught.message : "We could not generate your test. Try again.");
      return false;
    } finally {
      generationInFlight.current = false;
      setGenerating(false);
    }
  }, [generateAction, isFirstTest, planTier, testGenerationId]);

  const onGenerate = useCallback(async (
    config: TestConfig,
    context: TestGenerationContext,
  ) => {
    await runGeneration(config, context, false);
  }, [runGeneration]);

  const handleRegenerate = useCallback(async () => {
    if (!lastConfig || !lastContext || regenerateCount >= 2) return;
    const succeeded = await runGeneration(lastConfig, lastContext, true);
    if (succeeded) setRegenerateCount((count) => count + 1);
  }, [lastConfig, lastContext, regenerateCount, runGeneration]);

  const handleAccept = useCallback(async () => {
    if (!testGenerationId || !pendingOutput || acceptBusy) return;
    setError(null);
    setAcceptBusy(true);
    try {
      await acceptTest({ testGenerationId });
      track(ANALYTICS_EVENTS.testAccepted);
      try {
        await deleteUploadedFiles({ lessonImageIds });
      } catch (cleanupError) {
        console.error("Uploaded file cleanup failed", cleanupError);
      }
      setAcceptedOutput(pendingOutput);
      setPendingOutput(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "We could not save your test. Try again.");
    } finally {
      setAcceptBusy(false);
    }
  }, [acceptBusy, acceptTest, deleteUploadedFiles, lessonImageIds, pendingOutput, testGenerationId]);

  const handleSaveProfile = useCallback(async (teacherName: string, schoolName: string) => {
    try {
      await updateTeachingProfile({ teacherName, schoolName });
      return true;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "We could not save your test header.");
      return false;
    }
  }, [updateTeachingProfile]);

  const resetFlow = useCallback(async () => {
    if (lessonImageIds.length > 0 && !acceptedOutput) {
      try {
        await deleteUploadedFiles({ lessonImageIds });
      } catch (cleanupError) {
        console.error("Uploaded file cleanup failed", cleanupError);
      }
    }
    setAnalysis(null);
    setTestGenerationId(null);
    setPendingOutput(null);
    setAcceptedOutput(null);
    setLessonImageIds([]);
    setLastConfig(null);
    setLastContext(null);
    setRegenerateCount(0);
    setError(null);
    setActiveStep("lesson_content");
    setActiveSource(null);
    generatedRef.current = false;
    abandonmentTracked.current = false;
    progressRef.current = { source: null, step: "lesson_content" };
    setFormVersion((version) => version + 1);
  }, [acceptedOutput, deleteUploadedFiles, lessonImageIds]);

  const handleStepChange = useCallback((step: TestCreationStep, source: TestSource | null) => {
    setActiveStep(step);
    setActiveSource(source);
  }, []);

  const handleMeaningfulProgress = useCallback((progress: MeaningfulProgress) => {
    progressRef.current = progress;
  }, []);

  if (gate === "loading" || gate === "sync") {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-success" />
        <p className="text-sm text-muted-foreground">{gate === "sync" ? "Preparing your account…" : "Loading Tests…"}</p>
      </div>
    );
  }

  if (!me || isFirstTest === null) return null;
  const currentUser = me;
  const resolvedIsFirstTest = isFirstTest;

  const busy = analysing || generating;
  const hasGeneratedResult = Boolean((pendingOutput || acceptedOutput) && !busy);

  return (
    <main className="mx-auto flex w-full max-w-[1240px] flex-col gap-6 pb-10">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-success">Darsi Tests</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">Create a classroom-ready test</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-[15px]">Add your lesson content, choose how you want to assess it, and download a printable test with its answer key.</p>
        </div>
        <Link href="/history" className="inline-flex min-h-11 w-fit items-center gap-2 rounded-xl border border-border bg-card px-4 text-sm font-semibold text-foreground shadow-sm hover:border-success/40 hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success">
          <Library className="h-4 w-4 text-success" /> My resources
        </Link>
      </header>

      {!canUseTests ? (
        <ProFeatureLockOverlay
          title="Tests are included with Pro and your active trial"
          description="Create printable quizzes and global tests with answer keys. Upgrade to Pro after your trial to keep using Tests."
          className="min-h-[560px] w-full rounded-2xl"
          feature="tests"
        >
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(360px,0.95fr)]">
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6"><LockedFormPreview /></div>
            <TestValuePreview />
          </div>
        </ProFeatureLockOverlay>
      ) : hasGeneratedResult ? (
        <section className="min-w-0 rounded-2xl border border-success/30 bg-card p-3 shadow-sm sm:p-5">
          {error ? (
            <div role="alert" className="mb-4 flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-foreground">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold">Something needs attention</p>
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{error}</p>
              </div>
            </div>
          ) : null}

          {pendingOutput ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-xl border border-success/25 bg-success/5 p-4">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" />
                <div>
                  <p className="text-sm font-semibold text-foreground">Your classroom test is ready</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Review or edit sections, then save it to My resources to unlock student and answer-key downloads.</p>
                </div>
              </div>
              <TestResult
                key={`pending-${testGenerationId ?? "none"}-${regenerateCount}`}
                output={pendingOutput}
                exportLocked
                onNew={() => void resetFlow()}
                teacherName={currentUser.teacherName ?? currentUser.name ?? "—"}
                schoolName={currentUser.schoolName ?? ""}
                testId={testGenerationId ?? undefined}
                status="pending_acceptance"
                editablePending
                onOutputChange={setPendingOutput}
                stickyActions
                toolbarActions={
                  <>
                    <button type="button" disabled={acceptBusy} onClick={() => void handleAccept()} className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-success px-4 text-sm font-semibold text-success-foreground hover:bg-success/90 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success focus-visible:ring-offset-2">
                      {acceptBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      {acceptBusy ? "Saving…" : "Save and unlock downloads"}
                    </button>
                    <button type="button" disabled={regenerateCount >= 2 || generating} onClick={() => void handleRegenerate()} className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-border bg-card px-4 text-sm font-semibold text-foreground hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success focus-visible:ring-offset-2">
                      <RefreshCw className="h-4 w-4" /> {regenerateCount >= 2 ? "Retry limit reached" : `Regenerate · ${2 - regenerateCount} left`}
                    </button>
                    <button type="button" onClick={() => void resetFlow()} className="inline-flex min-h-10 items-center gap-2 rounded-xl px-3 text-sm font-medium text-muted-foreground hover:bg-destructive/5 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success focus-visible:ring-offset-2">
                      <Trash2 className="h-4 w-4" /> Discard
                    </button>
                  </>
                }
              />
            </div>
          ) : null}

          {acceptedOutput ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-xl border border-success/25 bg-success/5 p-4">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-success" />
                <div>
                  <p className="text-sm font-semibold text-foreground">Saved to My resources</p>
                  <p className="mt-1 text-xs text-muted-foreground">Download the student copy first, then keep the answer key for marking.</p>
                </div>
              </div>
              <TestResult
                output={acceptedOutput}
                onNew={() => void resetFlow()}
                teacherName={currentUser.teacherName ?? currentUser.name ?? "—"}
                schoolName={currentUser.schoolName ?? ""}
                status="accepted"
                stickyActions
              />
            </div>
          ) : null}
        </section>
      ) : (
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(360px,0.95fr)]">
          <section className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
            <TestForm
              key={formVersion}
              onAnalyse={onAnalyse}
              onGenerate={onGenerate}
              onSaveProfile={handleSaveProfile}
              analysing={analysing}
              generating={generating}
              hasGenerated={Boolean(pendingOutput || acceptedOutput)}
              analysis={analysis}
              canUseTests={canUseTests}
              teacherName={currentUser.teacherName}
              schoolName={currentUser.schoolName}
              isFirstTest={resolvedIsFirstTest}
              planTier={planTier}
              onStepChange={handleStepChange}
              onMeaningfulProgress={handleMeaningfulProgress}
              resumeDraft={resumeId && resumedTest && !resumedTest.output ? { grade: resumedTest.grade, testType: resumedTest.testType } : undefined}
            />
          </section>

          <section className="min-w-0 rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-5 lg:sticky lg:top-6">
            {error ? (
              <div role="alert" className="mb-4 flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-foreground">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold">Something needs attention</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{error}</p>
                </div>
              </div>
            ) : null}

            {busy ? (
              <GenerationProgress analysing={analysing} message={loadingMessages[messageIndex] ?? loadingMessages[0]} />
            ) : null}

            {!busy && !pendingOutput && !acceptedOutput ? (
              <TestValuePreview activeStep={activeStep} source={activeSource} />
            ) : null}

          </section>
        </div>
      )}
    </main>
  );
}

export default function NewTestPage() {
  return <Suspense fallback={<div role="status" className="py-16 text-center text-sm text-muted-foreground">Loading Tests…</div>}><NewTestContent /></Suspense>;
}

function TestValuePreview({ activeStep = "lesson_content", source = null }: { activeStep?: TestCreationStep; source?: TestSource | null }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-[#f7faf8]">
      <div className="border-b border-border bg-card px-5 py-4">
        <p className="text-sm font-semibold text-foreground">A classroom-ready test, not just a list of AI questions</p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Your complete printable test will appear here.</p>
      </div>
      <div className="p-4 sm:p-5">
        <div className="mx-auto max-w-md rounded-xl border border-slate-200 bg-white p-4 text-slate-700 shadow-sm" aria-label="Example structure of a Darsi classroom test">
          <div className="grid grid-cols-3 gap-2 border border-slate-300 text-[8px]">
            <div className="border-r border-slate-300 p-2">Full name: __________</div>
            <div className="border-r border-slate-300 p-2 text-center font-bold">ENGLISH TEST</div>
            <div className="p-2">Mark: ___ / 20</div>
            <div className="border-r border-t border-slate-300 p-2">Class: __________</div>
            <div className="border-r border-t border-slate-300 p-2 text-center">School year</div>
            <div className="border-t border-slate-300 p-2">Date: __________</div>
          </div>
          <PreviewSection number="I" title="COMPREHENSION" points="7 pts" items={["Reading passage", "A. Choose the right answer", "B. True or false", "C. Answer the questions"]} />
          <PreviewSection number="II" title="LANGUAGE" points="8 pts" items={["Vocabulary in context", "Grammar exercises", "Communication functions"]} />
          <PreviewSection number="III" title="WRITING" points="5 pts" items={["Guided classroom writing task", "Clear marking criteria"]} />
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-[9px] font-semibold text-green-800"><KeyRound className="h-3 w-3" /> Separate answer key included</div>
        </div>
        <div className="mt-4 flex flex-wrap justify-center gap-2 text-[11px] text-muted-foreground">
          <span className="rounded-md border border-border bg-card px-2 py-1">Printable A4</span>
          <span className="rounded-md border border-border bg-card px-2 py-1">Balanced marks</span>
          <span className="rounded-md border border-border bg-card px-2 py-1">Moroccan EFL structure</span>
        </div>
        {source ? <p className="mt-3 text-center text-xs text-success" aria-live="polite">{activeStep === "lesson_content" ? "Lesson source selected" : activeStep === "test_setup" ? "Next: shape the assessment" : "Ready to generate"}</p> : null}
      </div>
    </div>
  );
}

function PreviewSection({ number, title, points, items }: { number: string; title: string; points: string; items: string[] }) {
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between border-b border-green-800 pb-1 text-[9px] font-bold"><span>{number}. {title}</span><span>{points}</span></div>
      <div className="mt-1.5 space-y-1 text-[8px] text-slate-500">{items.map((item) => <p key={item}>{item}</p>)}</div>
    </div>
  );
}

function GenerationProgress({ analysing, message }: { analysing: boolean; message: string }) {
  return (
    <div className="flex min-h-[520px] flex-col items-center justify-center px-6 text-center" role="status" aria-live="polite">
      <div className="relative h-16 w-16" aria-hidden="true">
        <span className="absolute inset-0 rounded-full border-2 border-success/15" />
        <span className="absolute inset-0 rounded-full border-2 border-transparent border-t-success motion-safe:animate-spin" />
        <span className="absolute inset-2 flex items-center justify-center rounded-xl bg-success/10 text-success">
          <ClipboardCheck className="h-6 w-6" />
        </span>
      </div>
      <p className="mt-5 text-base font-semibold text-foreground">{analysing ? "Preparing questions from your lesson…" : "Creating your classroom test…"}</p>
      <p className="mt-2 text-sm text-muted-foreground">{message}</p>
      <p className="mt-5 max-w-xs text-xs leading-relaxed text-muted-foreground">Darsi is checking the sections, marks, difficulty, and answer key before showing your test.</p>
    </div>
  );
}

function LockedFormPreview() {
  return (
    <div className="space-y-5 opacity-80">
      <div className="grid grid-cols-3 gap-2">{["Lesson content", "Test setup", "Generate"].map((label, index) => <div key={label} className={cn("rounded-xl border p-3 text-xs font-semibold", index === 0 ? "border-success bg-success/10" : "border-border bg-muted/20 text-muted-foreground")}>{index + 1}. {label}</div>)}</div>
      <div><p className="text-xl font-semibold text-foreground">Add the lesson you want to assess</p><p className="mt-1 text-sm text-muted-foreground">Upload clear textbook pages or lesson screenshots.</p></div>
      <div className="flex min-h-40 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-input bg-muted/20"><Sparkles className="h-6 w-6 text-success" /><p className="mt-3 text-sm font-semibold">Upload or try a sample lesson</p></div>
    </div>
  );
}
